import { DEFAULT_LOCALE, SUPPORTED_LOCALES } from './locales.js';

/**
 * Map whatever the host put in the `locale` URL param onto a locale that actually exists.
 *
 * The one guarantee: the return value is always an entry of `supported`, spelled exactly as
 * `supported` spells it. Nothing downstream — `createI18n({ locale })`, `i18n.global.locale.value`,
 * a `messages[locale]` lookup, `Intl.DateTimeFormat` — can then be handed a key with no bundle.
 *
 * Resolution order:
 *   1. non-string / empty / whitespace / the host's `'-'` sentinel → `fallback`
 *   2. case-insensitive EXACT match → the **canonical** entry (`'zh-hant'` → `'zh-Hant'`)
 *   3. base-language match, `-` and `_` both accepted (`'en-GB'`/`'en_GB'` → `'en'`)
 *   4. otherwise → `fallback` (`'zh'`, `'pt'`, `'xx-YY'` → `'en'`)
 *
 * Step 3 is base-language only, never "closest region": `'zh'` does NOT pick `'zh-Hans'`, because
 * choosing between Simplified and Traditional for a caller who did not say is a guess that shows up
 * as wrong characters. `'pt'` likewise does not pick `'pt-BR'`.
 *
 * Replaces the app's old `utils/localeMapper.js`, which mapped `'zh-Hant'` → `'zhHant'` (a key no
 * bundle has) and `'-'` → `'defaultLocale'` (an all-empty bundle that was also the `fallbackLocale`),
 * so anything it missed rendered as `""` rather than falling back to English.
 *
 * @param {unknown} input Raw locale, e.g. a URL param. Anything at all is safe to pass.
 * @param {string[]} [supported=SUPPORTED_LOCALES] Canonical locales, in canonical spelling.
 * @param {string} [fallback=DEFAULT_LOCALE] Returned when nothing matches.
 * @returns {string} An entry of `supported` (or `fallback`).
 */
export function normalizeLocale(input, supported = SUPPORTED_LOCALES, fallback = DEFAULT_LOCALE) {
  if (typeof input !== 'string') return fallback;

  const raw = input.trim();
  if (!raw || raw === '-') return fallback;

  const lower = raw.toLowerCase();

  // Exact match, case-insensitively — but return the CANONICAL spelling, since that is the
  // key `messages` is indexed by ('zh-hant' must become 'zh-Hant', not stay lower-cased).
  const exact = supported.find((locale) => locale.toLowerCase() === lower);
  if (exact) return exact;

  // Base language: 'en-gb'/'en_GB' → 'en'. vue-i18n's implicit region fallback would cover
  // the hyphen form at translate time, but not the underscore form and not a `messages[locale]`
  // lookup, so resolve it here instead of relying on it.
  const base = lower.split(/[-_]/)[0];
  const byBase = supported.find((locale) => locale.toLowerCase() === base);
  if (byBase) return byBase;

  return fallback;
}
